// Wait Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Pause as WaitIcon,
  Timer as TimerIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

interface WaitNodeData {
  label: string;
  description?: string;
  waitType?: 'duration' | 'until' | 'approval';
  status?: 'idle' | 'waiting' | 'completed' | 'timeout';
  duration?: number;
  unit?: 'seconds' | 'minutes' | 'hours';
  remainingTime?: number;
  startTime?: string;
}

const WaitNode: React.FC<NodeProps<WaitNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getWaitIcon = () => {
    switch (data.waitType) {
      case 'duration':
        return <TimerIcon />;
      case 'until':
        return <ScheduleIcon />;
      default:
        return <WaitIcon />;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'waiting':
        return '#FFF3E0';
      case 'completed':
        return '#E8F5E9';
      case 'timeout':
        return '#FFEBEE';
      default:
        return '#F5F5F5';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'waiting':
        return '#FF9800';
      case 'completed':
        return '#4CAF50';
      case 'timeout':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  const formatDuration = () => {
    if (!data.duration || !data.unit) return '';
    return `${data.duration} ${data.unit}`;
  };

  const formatRemainingTime = () => {
    if (!data.remainingTime) return '';
    
    const seconds = Math.floor(data.remainingTime / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getProgress = () => {
    if (!data.duration || !data.remainingTime) return 0;
    const totalMs = data.duration * (data.unit === 'hours' ? 3600000 : data.unit === 'minutes' ? 60000 : 1000);
    return Math.max(0, Math.min(100, ((totalMs - data.remainingTime) / totalMs) * 100));
  };

  return (
    <Box sx={{ minWidth: 180 }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />
      
      <Card
        sx={{
          backgroundColor: getStatusColor(),
          border: `2px solid ${getBorderColor()}`,
          borderRadius: 2,
          minHeight: 100,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 6 : 2,
          position: 'relative',
          '&:hover': {
            boxShadow: 8,
            transform: 'translateY(-2px)'
          }
        }}
      >
        {/* Pulsing border for waiting state */}
        {data.status === 'waiting' && (
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              borderRadius: 2,
              border: `2px solid ${getBorderColor()}60`,
              animation: 'pulse 2s ease-in-out infinite',
              pointerEvents: 'none'
            }}
          />
        )}

        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header with Icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '50%',
                backgroundColor: `${getBorderColor()}20`,
                color: getBorderColor(),
                mr: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: data.status === 'waiting' ? 'breathe 2s ease-in-out infinite' : 'none'
              }}
            >
              {getWaitIcon()}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  lineHeight: 1.2
                }}
              >
                {data.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                {data.waitType === 'duration' ? 'Timed Wait' : 'Wait Until'}
              </Typography>
            </Box>
          </Box>

          {/* Description */}
          {data.description && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1.5,
                fontSize: '0.8rem',
                lineHeight: 1.3
              }}
            >
              {data.description}
            </Typography>
          )}

          {/* Duration info */}
          {data.duration && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={formatDuration()}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  backgroundColor: `${getBorderColor()}20`,
                  color: getBorderColor()
                }}
              />
            </Box>
          )}

          {/* Progress bar for waiting state */}
          {data.status === 'waiting' && data.remainingTime !== undefined && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={getProgress()}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: `${getBorderColor()}20`,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getBorderColor(),
                    borderRadius: 3
                  }
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  mt: 0.5,
                  display: 'block'
                }}
              >
                {formatRemainingTime()} remaining
              </Typography>
            </Box>
          )}

          {/* Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={data.status || 'idle'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: getBorderColor(),
                color: 'white',
                textTransform: 'uppercase'
              }}
            />
            
            {data.startTime && data.status === 'waiting' && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.65rem'
                }}
              >
                Since {new Date(data.startTime).toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />

      {/* Waiting indicator */}
      {data.status === 'waiting' && (
        <Box
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: '#FF9800',
            animation: 'glow 2s ease-in-out infinite',
            zIndex: 10
          }}
        />
      )}

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          
          @keyframes breathe {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
          }
          
          @keyframes glow {
            0%, 100% {
              box-shadow: 0 0 5px rgba(255, 152, 0, 0.5);
            }
            50% {
              box-shadow: 0 0 20px rgba(255, 152, 0, 0.8);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(WaitNode);